import { useQuery } from "@tanstack/react-query";
import { apiGet } from "./client";
import { useTicketSize, type TicketSize } from "../ticket-size";
import type { RiskGrade } from "./state-of-play";

export type SegmentLevel = "Industry" | "Office" | "Portfolio";
export type RavBasis = "realized" | "projected";

export const SEGMENT_LEVELS: SegmentLevel[] = ["Industry", "Office", "Portfolio"];
export const RAV_BASES: RavBasis[] = ["realized", "projected"];

/** Header for the amount column — the figure itself changes with the basis. */
export function amountLabel(basis: RavBasis) {
  return basis === "realized" ? "Financed Amount" : "Net Investment";
}

export interface SegmentRow {
  id: string;
  // Exactly one of office/industry is set, matching which column this node was
  // grouped by on the backend -- an Office-grain row spans every industry, and
  // an Industry-grain row spans every office, so the other field is absent
  // rather than a copy of this row's own label.
  office?: string;
  industry?: string;
  grade?: RiskGrade;
  /** Origination amount under Realized; current outstanding balance under Projected. */
  financed: number;
  yield: number;
  /** Weighted average booked FTP (realized) or ftpForWal on today's curve (projected). */
  ftp: number;
  expectedLoss: number;
  opex: number;
  externalCommission: number;
  /** Actual dollars paid (realized) or grossRav × internalCommissionRate (projected). */
  internalCommission: number;
  internalCommissionDollars: number;
  internalCommissionRate: number;
  targetNetMargin: number;
  /** Financed-amount-weighted (realized) or net-investment-weighted (projected) required yield. */
  requiredYield: number;
  /** yield - ftp - expectedLoss - opex - externalCommission */
  grossRav: number;
  /** grossRav - internalCommission */
  netRav: number;
  ravBasis: RavBasis;
  walYears: number;
  credibility: { z: number; tier: string; peerCount: number };
  scope: "priced" | "fixed";
}

/** Hierarchical wrapper so the table can drill Office → Industry → Grade. */
export interface SegmentNode extends SegmentRow {
  subRows?: SegmentNode[];
}

export interface SegmentsFilters {
  /** office_number */
  office?: number;
  industry?: string;
}

export interface SegmentsSummary {
  level: SegmentLevel;
  ravBasis: RavBasis;
  rows: SegmentNode[];
}


export const segmentsKeys = {
  summary: (level: SegmentLevel, basis: RavBasis, filters: SegmentsFilters, ticket?: TicketSize) =>
    ["segments", "summary", level, basis, filters.office ?? "all", filters.industry ?? "all", ticket ?? "all"] as const,
};

export function fetchSegmentsSummary(
  level: SegmentLevel,
  basis: RavBasis,
  filters: SegmentsFilters,
  ticket?: TicketSize,
) {
  return apiGet<SegmentsSummary>("/segments/summary", {
    level: level.toLowerCase(),
    basis,
    office: filters.office != null ? String(filters.office) : undefined,
    industry: filters.industry,
    ticket,
  });
}

export function useSegmentsSummary(
  level: SegmentLevel = "Industry",
  filters: SegmentsFilters & { ravBasis?: RavBasis } = {},
) {
  const basis = filters.ravBasis ?? "projected";
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: segmentsKeys.summary(level, basis, filters, ticket),
    queryFn: () => fetchSegmentsSummary(level, basis, filters, ticket),
    staleTime: 60_000,
  });
}
